'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { useState, useEffect } from 'react';
import { BaseTranslations } from '../../baseTranslations';
import { AttrKeyValue } from '../../types/attributes';
import { CmsProduct } from '../../types/cmsProduct';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import Button from '../Button';
import { CmsImage } from '../CmsImage';
import { FeLink } from '../FeLink';
import ArrowUp from '../icons/ArrowUp';
import { RichText } from '../RichText';
import { ProductApplyCta } from './ProductApplyCta';
import { ModeRating, RatingComponent } from './ProductRating';

type ProductCardStickyProps = {
  attributes?: Pick<CmsProduct, 'attributes'>['attributes'];
  product_image?: string;
  product_name?: string;
  rate_link?: string;
  rate_score?: number;
  description?: string;
  /**
   * The product itself, alongside its spread fields: the apply CTA resolves its
   * own destination from it, so the click can be intercepted (CMS-1740).
   */
  product: CmsProduct;
  ctaLinkOverride: string | undefined;
  pushDataLayer: PushDataLayerFn;
};
const ProductCardSticky: React.FC<ProductCardStickyProps> = (props) => {
  const {
    attributes,
    product_image,
    rate_score,
    product_name,
    rate_link,
    description,
    product,
    ctaLinkOverride,
    pushDataLayer,
  } = props;
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const [isScreenSizeXs, setIsScreenSizeXs] = useState(false);
  const [isHeaderOpen, setIsHeaderOpen] = useState(false);

  const handleOpenExpand = () => setIsHeaderOpen(!isHeaderOpen);

  useEffect(() => {
    const checkScreenSize = () => setIsScreenSizeXs(window.innerWidth < 1024);

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const descriptionClasses = classNames('relative overflow-hidden lg:hidden', {
    'h-auto': isHeaderOpen,
    'h-10': !isHeaderOpen,
  });

  const gradientClasses = classNames({
    'gradient-page-header absolute left-0 bottom-0 h-2 w-full': !isHeaderOpen,
    hidden: isHeaderOpen,
  });

  const topBadge = attributes
    ?.filter(
      (attr): attr is AttrKeyValue =>
        attr?.__typename === 'ComponentAttributesAttrKeyValue' &&
        attr.name?.name === 'PRODUCT_CARD_TOP_BADGE',
    )
    .flatMap(
      (keyValue) =>
        keyValue.value?.flatMap((value) => value?.values || []) || [],
    );

  return (
    <div
      className={classNames(
        'relative z-0 grow basis-4/12 px-4 py-8 pb-4 bg-white lg:mx-8 lg:min-w-91 lg:px-0 lg:py-8 lg:!pr-0 lg:bg-transparent xl:w-91 xl:py-10 xl:!pb-8',
      )}
    >
      <div className="lg:sticky lg:top-0 lg:rounded-3xl lg:p-6 lg:shadow lg:bg-white">
        <div className="flex flex-wrap">
          {topBadge?.map((badge, index) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="mb-2 mr-2 flex whitespace-normal rounded-lg border-t-brand-primary-dark px-2 font-semibold bg-fill/primary-light text-sm lg:mb-4 lg:text-base"
            >
              {badge?.value}
            </span>
          ))}
        </div>

        <h3 className="mb-4 font-bold text-gray-900 text-2xl leading-line-height/xl sm:mb-6 sm:text-3xl sm:leading-line-height/3xl lg:hidden lg:text-4xl lg:leading-line-height/4xl">
          {product_name}
        </h3>

        <div className="mb-4 flex items-center lg:flex-col">
          <div className="flex max-w-80 items-start lg:w-full">
            <CmsImage
              sizes={{ _: 316 }}
              src={product_image ?? ''}
              alt="product"
              width={320}
              height={210}
              className="inline h-22 w-35 object-contain lg:h-[210px] lg:w-full"
            />
          </div>

          {rate_score && rate_score >= 1 && (
            <div className="ml-4 cursor-pointer lg:m-0">
              <FeLink href={rate_link ?? ''}>
                <RatingComponent
                  mode={
                    isScreenSizeXs ? ModeRating.VERTICAL : ModeRating.HORIZONTAL
                  }
                  {...props}
                />
              </FeLink>
            </div>
          )}
          <ProductApplyCta
            className="hidden w-full lg:block"
            product={product}
            ctaLinkOverride={ctaLinkOverride}
            pushDataLayer={pushDataLayer}
          />
        </div>
      </div>

      {description && (
        <div>
          <div className={descriptionClasses}>
            <RichText className="mb-4 flex flex-col gap-2 alpha-body-1">
              {description}
            </RichText>
            <div className={gradientClasses}></div>
          </div>
          {description.length >= 60 && (
            <div className="flex justify-end lg:hidden">
              <Button
                onClick={handleOpenExpand}
                variant="text"
                className="pl-0 text-gray-600"
                endIcon={
                  isHeaderOpen ? (
                    <ArrowUp className="text-link" />
                  ) : (
                    <ArrowUp className="rotate-180 transition-transform text-link" />
                  )
                }
              >
                {isHeaderOpen
                  ? tr('see_less', staticText)?.toLowerCase()
                  : tr('see_more', staticText)?.toLowerCase()}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCardSticky;
